import { useNavigation } from "@react-navigation/native";
import ContactIcon from "assets/images/defaultIcons/contact.svg";
import DataIcon from "assets/images/defaultIcons/data.svg";
import LocationIcon from "assets/images/defaultIcons/location.svg";
import TeamIcon from "assets/images/defaultIcons/team.svg";
import theme from "assets/theme";
import { formatDatetime } from "helpers/shared.helpers";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import AlertService from "services/Alert.service";
import { ALERT_TYPE_INFO } from "shared/constants";
import {
  SCREEN_DASHBOARD_ALERTS,
  SCREEN_DASHBOARD_PLACES,
  SCREEN_DASHBOARD_PLATFORMS,
  SCREEN_DASHBOARD_TEAMS,
} from "web/constants";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import SortSelector from "webComponentsFormElements/inputs/SortSelector.component";
import { AlertContext } from "webContext/Alert.context";
import { AuthContext } from "webContext/Auth.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    backgroundColor: theme.color.background,
    flex: 1,
    overflow: "auto",
  },
  mainContainer: {
    flexDirection: "row",
    gap: 20,
    padding: 20,
  },
  leftColumn: {
    flex: 2,
  },
  rightColumn: {
    flex: 1,
    minWidth: 300,
  },
  title: {
    fontSize: theme.font.size.title,
    fontWeight: theme.font.weight.bold,
    color: theme.color.darkGrey,
    marginBottom: 20,
  },
  welcomeText: {
    fontSize: theme.font.size.body,
    color: theme.color.darkGrey,
    marginBottom: 15,
    lineHeight: 24,
  },
  contentSection: {
    marginTop: 30,
    padding: 20,
    backgroundColor: theme.color.white,
    borderRadius: 8,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  sectionTitle: {
    fontSize: theme.font.size.subtitle,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.darkGrey,
    marginBottom: 10,
  },
  sectionText: {
    fontSize: theme.font.size.body,
    color: theme.color.darkGrey,
    lineHeight: 22,
  },
  statsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
    marginBottom: 20,
  },
  statCard: {
    backgroundColor: theme.color.white,
    borderRadius: 8,
    padding: 20,
    alignItems: "center",
    minWidth: 120,
    flex: 1,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  statNumber: {
    fontSize: 32,
    fontWeight: theme.font.weight.bold,
    color: theme.color.primary,
    marginBottom: 5,
  },
  statLabel: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    textAlign: "center",
    fontWeight: theme.font.weight.medium,
  },
  platformsContainer: {
    marginBottom: 25,
  },
  platformsHeader: {
    fontSize: 24,
    fontWeight: theme.font.weight.semibold,
    marginBottom: 15,
  },
  platformsList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
  },
  platformCard: {
    backgroundColor: theme.color.white,
    borderRadius: 8,
    padding: 15,
    alignItems: "center",
    minWidth: 100,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  platformCardPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
  platformLogo: {
    width: 40,
    height: 40,
    marginBottom: 8,
  },
  platformIcon: {
    width: 40,
    height: 40,
    fill: theme.color.primary,
    marginBottom: 8,
  },
  platformName: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    textAlign: "center",
    fontWeight: theme.font.weight.medium,
  },
  createdAt: {
    fontSize: theme.font.size.small - 2,
    color: theme.color.mediumGrey,
    textAlign: "center",
    marginTop: 2,
  },
  platformCardContent: {
    position: "relative",
    alignItems: "center",
  },
  alertBadge: {
    position: "absolute",
    top: -8,
    right: -8,
    backgroundColor: "#ff4444",
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: theme.color.white,
  },
  alertBadgeText: {
    color: theme.color.white,
    fontSize: 12,
    fontWeight: theme.font.weight.bold,
    textAlign: "center",
  },
  teamsContainer: {
    marginBottom: 25,
  },
  teamsHeader: {
    fontSize: 24,
    fontWeight: theme.font.weight.semibold,
    marginBottom: 15,
  },
  teamsList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
  },
  teamCard: {
    backgroundColor: theme.color.white,
    borderRadius: 8,
    padding: 15,
    alignItems: "center",
    minWidth: 100,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  teamCardPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
  teamIcon: {
    width: 40,
    height: 40,
    fill: theme.color.primary,
    marginBottom: 8,
  },
  teamName: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    textAlign: "center",
    fontWeight: theme.font.weight.medium,
  },
  memberCount: {
    fontSize: theme.font.size.small - 2,
    color: theme.color.darkGrey,
    textAlign: "center",
    marginTop: 2,
  },
  teamCardContent: {
    position: "relative",
    alignItems: "center",
  },
  officesContainer: {
    marginBottom: 25,
  },
  officesHeader: {
    fontSize: 24,
    fontWeight: theme.font.weight.semibold,
    marginBottom: 15,
  },
  officesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
  },
  officeCard: {
    backgroundColor: theme.color.white,
    borderRadius: 8,
    padding: 15,
    alignItems: "center",
    minWidth: 100,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  officeCardPressed: {
    opacity: 0.7,
    transform: [{ scale: 0.98 }],
  },
  locationIcon: {
    width: 40,
    height: 40,
    fill: theme.color.primary,
    marginBottom: 8,
  },
  officeName: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    textAlign: "center",
    fontWeight: theme.font.weight.medium,
  },
  officeCardContent: {
    position: "relative",
    alignItems: "center",
  },
  alertsContainer: {
    backgroundColor: theme.color.white,
    borderRadius: 8,
    padding: 15,
    elevation: 3,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  alertsHeader: {
    fontSize: 24,
    fontWeight: theme.font.weight.semibold,
    marginBottom: 15,
  },
  alertCard: {
    backgroundColor: theme.color.background,
    borderWidth: 2,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  alertCardPressed: {
    opacity: 0.7,
  },
  alertTitle: {
    fontSize: theme.font.size.small,
    fontWeight: theme.font.weight.semibold,
  },
  alertDescription: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    marginBottom: 8,
    lineHeight: 18,
  },
  alertMeta: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    marginBottom: 10,
  },
  alertInfo: {
    marginBottom: 8,
  },
  alertInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  alertInfoText: {
    fontSize: theme.font.size.small - 1,
    color: theme.color.darkGrey,
    marginLeft: 4,
  },
  alertInfoIcon: {
    width: 16,
    height: 16,
  },
  platformIconsContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  platformIconSmall: {
    width: 20,
    height: 20,
    marginRight: 4,
  },
  alertButtonContainer: {
    alignItems: "flex-end",
  },
  noAlertsText: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    fontStyle: "italic",
    textAlign: "center",
  },
  loadingContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 10,
    color: theme.color.darkGrey,
  },
  dismissedText: {
    color: theme.color.darkGrey,
    fontStyle: "italic",
  },
  alertTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 8,
  },
  alertTitleText: {
    flex: 1,
  },
  expiredBadge: {
    backgroundColor: theme.color.red,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  expiredBadgeText: {
    color: theme.color.white,
    fontSize: 11,
    fontWeight: theme.font.weight.bold,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  staleBanner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.color.lightGrey,
    borderRadius: 8,
    marginBottom: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 12,
  },
  staleText: {
    flex: 1,
    fontSize: 13,
    color: theme.color.darkGrey,
    fontWeight: theme.font.weight.medium,
  },
});

export default function HomeScreen() {
  const [isLoading, setIsLoading] = useState(true);
  const [platformsSortBy, setPlatformsSortBy] = useState("nameAsc");
  const [teamsSortBy, setTeamsSortBy] = useState("nameAsc");
  const [locationsSortBy, setLocationsSortBy] = useState("nameAsc");
  const [resolvingAlert, setResolvingAlert] = useState(null);
  const [resolutionText, setResolutionText] = useState("");
  const navigation = useNavigation();
  const { getAlerts, sortedAllAlerts } = useContext(AlertContext);
  const { isAdmin, isMessagingManager } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const {
    getTeams,
    teamList,
    getLocations,
    locationList,
    getOrganizationSystems,
    organizationSystems,
    organizationColor,
    setCurrentRoute,
  } = useContext(OrganizationContext);

  // Helper function to strip HTML tags and decode entities
  const stripHtml = useCallback((html) => {
    if (!html) {
      return "";
    }

    // Remove HTML tags
    const withoutTags = html.replace(/<[^>]*>/g, "");

    // Decode common HTML entities
    const entityMap = {
      "&amp;": "&",
      "&lt;": "<",
      "&gt;": ">",
      // prettier-ignore
      "&quot;": "\"",
      "&#39;": "'",
      "&nbsp;": " ",
    };

    return withoutTags.replace(
      /&[#\w]+;/g,
      (entity) => entityMap[entity] || entity,
    );
  }, []);

  const sortItems = useCallback((items, sortBy) => {
    return [...items].sort((a, b) => {
      if (sortBy === "createdAtOldest") {
        const dateA = new Date(a.createdAt || a.changeDatetime);
        const dateB = new Date(b.createdAt || b.changeDatetime);
        return dateA - dateB; // Oldest first
      } else if (sortBy === "createdAtNewest") {
        const dateA = new Date(a.createdAt || a.changeDatetime);
        const dateB = new Date(b.createdAt || b.changeDatetime);
        return dateB - dateA; // Newest first
      } else if (sortBy === "nameDesc") {
        const nameA = (a.name || a.title || "").toLowerCase();
        const nameB = (b.name || b.title || "").toLowerCase();
        return nameB.localeCompare(nameA); // Z-A
      } else {
        // Default to nameAsc
        const nameA = (a.name || a.title || "").toLowerCase();
        const nameB = (b.name || b.title || "").toLowerCase();
        return nameA.localeCompare(nameB); // A-Z
      }
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          getAlerts(),
          getTeams(),
          getLocations(),
          getOrganizationSystems(),
        ]);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [getAlerts, getTeams, getLocations, getOrganizationSystems]);

  // Convert alerts map to array for counting
  const alertsArray = Array.from(sortedAllAlerts?.values() || []);
  const activeAlertsArray = alertsArray.filter((alert) => !!alert.isActive);

  const staleInfoAlerts = useMemo(() => {
    if (!isAdmin && !isMessagingManager) {
      return [];
    }
    const now = new Date();
    return alertsArray.filter(
      (alert) =>
        alert.type === ALERT_TYPE_INFO &&
        alert.isActive &&
        !alert.isDeleted &&
        alert.expirationDatetime &&
        new Date(alert.expirationDatetime) < now,
    );
  }, [alertsArray, isAdmin, isMessagingManager]);

  const handleBatchResolve = useCallback(async () => {
    try {
      await AlertService.dismissAlerts({
        alertIds: staleInfoAlerts.map((a) => a.alertId),
        resolution: "Batch resolved — stale info messages.",
      });
      await getAlerts();
      showToast(
        `${staleInfoAlerts.length} stale info message${staleInfoAlerts.length > 1 ? "s" : ""} resolved.`,
        "success",
      );
    } catch {
      showToast("Failed to batch resolve info messages.", "error");
    }
  }, [staleInfoAlerts, getAlerts, showToast]);

  // Calculate alert counts per platform
  const platformAlertCounts = useMemo(() => {
    const counts = new Map();

    // Initialize counts for all platforms
    organizationSystems.forEach((platform) => {
      counts.set(platform.organizationSystemId, 0);
    });

    // Count active alerts for each platform
    activeAlertsArray.forEach((alert) => {
      if (alert.isActive && alert._associations?.systemAlertMap) {
        alert._associations.systemAlertMap.forEach((systemAlert) => {
          const platform = systemAlert._associations?.organizationSystem;
          if (platform && platform.organizationSystemId) {
            const currentCount = counts.get(platform.organizationSystemId) || 0;
            counts.set(platform.organizationSystemId, currentCount + 1);
          } else {
            console.log("  - No platform found in systemAlert:", systemAlert);
          }
        });
      } else {
        console.log(
          `Skipping alert ${alert.alertId}: isActive=${alert.isActive}, hasSystemAlertMap=${!!alert._associations?.systemAlertMap}`,
        );
      }
    });

    return counts;
  }, [activeAlertsArray, organizationSystems]);

  // Calculate alert counts per team
  const teamAlertCounts = useMemo(() => {
    const counts = new Map();

    // Initialize counts for all teams
    teamList.forEach((team) => {
      counts.set(team.teamId, 0);
    });

    // Count active alerts for each team
    activeAlertsArray.forEach((alert) => {
      if (alert.isActive && alert._associations?.teamAlertMap) {
        alert._associations.teamAlertMap.forEach((teamAlert) => {
          const currentCount = counts.get(teamAlert.teamId) || 0;
          counts.set(teamAlert.teamId, currentCount + 1);
        });
      }
    });

    return counts;
  }, [activeAlertsArray, teamList]);

  // Calculate alert counts per location (office)
  const locationAlertCounts = useMemo(() => {
    const counts = new Map();

    // Initialize counts for all locations
    locationList.forEach((location) => {
      counts.set(location.locationId, 0);
    });

    // Count active alerts for each location
    activeAlertsArray.forEach((alert) => {
      if (alert.isActive && alert._associations?.locationAlertMap) {
        alert._associations.locationAlertMap.forEach((locationAlert) => {
          const currentCount = counts.get(locationAlert.locationId) || 0;
          counts.set(locationAlert.locationId, currentCount + 1);
        });
      }
    });

    return counts;
  }, [activeAlertsArray, locationList]);

  // Sorted arrays
  const sortedOrganizationSystems = useMemo(
    () => sortItems(organizationSystems, platformsSortBy),
    [organizationSystems, platformsSortBy, sortItems],
  );

  const sortedTeamList = useMemo(
    () => sortItems(teamList, teamsSortBy),
    [teamList, teamsSortBy, sortItems],
  );

  const sortedLocationList = useMemo(
    () => sortItems(locationList, locationsSortBy),
    [locationList, locationsSortBy, sortItems],
  );

  const handlePlatformPress = (organizationSystemId) => {
    setCurrentRoute(SCREEN_DASHBOARD_PLATFORMS);
    navigation.navigate(SCREEN_DASHBOARD_PLATFORMS, { organizationSystemId });
  };

  const handleTeamPress = (teamId) => {
    setCurrentRoute(SCREEN_DASHBOARD_TEAMS);
    navigation.navigate(SCREEN_DASHBOARD_TEAMS, {
      selectedTeamId: teamId,
      openModal: true,
    });
  };

  const handleOfficePress = (locationId) => {
    setCurrentRoute(SCREEN_DASHBOARD_PLACES);
    navigation.navigate(SCREEN_DASHBOARD_PLACES, { locationId });
  };

  const handleAlertPress = (alertId, alertType) => {
    setCurrentRoute(SCREEN_DASHBOARD_ALERTS);
    navigation.navigate(SCREEN_DASHBOARD_ALERTS, { alertId, alertType });
  };

  const handleResolveAlert = useCallback((alert) => {
    setResolvingAlert(alert);
    setResolutionText("");
  }, []);

  const handleConfirmResolve = useCallback(async () => {
    try {
      await AlertService.upsertAlert({
        alert: {
          alertId: resolvingAlert.alertId,
          isActive: false,
          title: resolvingAlert.title,
          description: resolvingAlert.description || null,
          startDateTime: formatDatetime(resolvingAlert.startDatetime),
          resolution: resolutionText.trim() || "No resolution provided",
        },
      });
      await getAlerts();
      setResolvingAlert(null);
      setResolutionText("");
      showToast(`Alert "${resolvingAlert.title}" resolved successfully.`, "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Failed to resolve alert:", error);
      }
      showToast("Failed to resolve alert.", "error");
    }
  }, [resolvingAlert, resolutionText, getAlerts, showToast]);

  if (isLoading) {
    return (
      <View style={[classes.root, classes.loadingContainer]}>
        <ActivityIndicator size="large" color={theme.color.primary} />
        <Text style={classes.loadingText}>Loading dashboard...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={classes.root}>
      <View style={classes.mainContainer}>
        <View style={classes.leftColumn}>
          {/* <Text style={classes.title}>Welcome to Your Dashboard</Text>

          <Text style={classes.welcomeText}>
            This is your Business Continuity Planning dashboard. Here's an overview of your current data.
          </Text> */}

          {organizationSystems.length > 0 && (
            <View style={classes.platformsContainer}>
              <Text
                style={[classes.platformsHeader, { color: organizationColor }]}
              >
                Platforms
              </Text>
              <SortSelector
                sortBy={platformsSortBy}
                onSortChange={setPlatformsSortBy}
                organizationColor={organizationColor}
              />
              <View style={classes.platformsList}>
                {sortedOrganizationSystems.map((platform) => {
                  const alertCount =
                    platformAlertCounts.get(platform.organizationSystemId) || 0;

                  return (
                    <Pressable
                      key={platform.organizationSystemId}
                      style={({ pressed }) => [
                        classes.platformCard,
                        {
                          borderWidth: 2,
                          borderColor: organizationColor,
                        },
                        pressed && classes.platformCardPressed,
                      ]}
                      onPress={() =>
                        handlePlatformPress(platform.organizationSystemId)
                      }
                    >
                      <View style={classes.platformCardContent}>
                        {platform.signedFileUrl ? (
                          <Image
                            source={{ uri: platform.signedFileUrl }}
                            resizeMode="contain"
                            style={classes.platformLogo}
                          />
                        ) : (
                          <DataIcon style={classes.platformIcon} />
                        )}
                        {alertCount > 0 && (
                          <View style={classes.alertBadge}>
                            <Text style={classes.alertBadgeText}>
                              {alertCount > 99 ? "99+" : alertCount}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={classes.platformName} numberOfLines={2}>
                        {platform.title}
                      </Text>
                      {(platform.createdAt || platform.changeDatetime) && (
                        <Text style={classes.createdAt}>
                          Created{" "}
                          {moment(
                            platform.createdAt || platform.changeDatetime,
                          ).format("MMM DD, YYYY")}
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          {teamList.length > 0 && (
            <View style={classes.teamsContainer}>
              <Text style={[classes.teamsHeader, { color: organizationColor }]}>
                Teams
              </Text>
              <SortSelector
                sortBy={teamsSortBy}
                onSortChange={setTeamsSortBy}
                organizationColor={organizationColor}
              />
              <View style={classes.teamsList}>
                {sortedTeamList.map((team) => {
                  const alertCount = teamAlertCounts.get(team.teamId) || 0;

                  return (
                    <Pressable
                      key={team.teamId}
                      style={({ pressed }) => [
                        classes.teamCard,
                        {
                          borderWidth: 2,
                          borderColor: organizationColor,
                        },
                        pressed && classes.teamCardPressed,
                      ]}
                      onPress={() => handleTeamPress(team.teamId)}
                    >
                      <View style={classes.teamCardContent}>
                        <TeamIcon style={classes.teamIcon} />
                        {alertCount > 0 && (
                          <View style={classes.alertBadge}>
                            <Text style={classes.alertBadgeText}>
                              {alertCount > 99 ? "99+" : alertCount}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={classes.teamName} numberOfLines={2}>
                        {team.name}
                      </Text>
                      <Text style={classes.memberCount}>
                        {team._associations?.TeamPersons?.length || 0} members
                      </Text>
                      {(team.createdAt || team.changeDatetime) && (
                        <Text style={classes.createdAt}>
                          Created{" "}
                          {moment(team.createdAt || team.changeDatetime).format(
                            "MMM DD, YYYY",
                          )}
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          {locationList.length > 0 && (
            <View style={classes.officesContainer}>
              <Text
                style={[classes.officesHeader, { color: organizationColor }]}
              >
                Places
              </Text>
              <SortSelector
                sortBy={locationsSortBy}
                onSortChange={setLocationsSortBy}
                organizationColor={organizationColor}
              />
              <View style={classes.officesList}>
                {sortedLocationList.map((location) => {
                  const alertCount =
                    locationAlertCounts.get(location.locationId) || 0;

                  return (
                    <Pressable
                      key={location.locationId}
                      style={({ pressed }) => [
                        classes.officeCard,
                        {
                          borderWidth: 2,
                          borderColor: organizationColor,
                        },
                        pressed && classes.officeCardPressed,
                      ]}
                      onPress={() => handleOfficePress(location.locationId)}
                    >
                      <View style={classes.officeCardContent}>
                        {location.signedFileUrl ? (
                          <Image
                            source={{ uri: location.signedFileUrl }}
                            resizeMode="contain"
                            style={classes.platformLogo}
                          />
                        ) : (
                          <LocationIcon style={classes.locationIcon} />
                        )}
                        {alertCount > 0 && (
                          <View style={classes.alertBadge}>
                            <Text style={classes.alertBadgeText}>
                              {alertCount > 99 ? "99+" : alertCount}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={classes.officeName} numberOfLines={2}>
                        {location.name}
                      </Text>
                      {(location.createdAt || location.changeDatetime) && (
                        <Text style={classes.createdAt}>
                          Created{" "}
                          {moment(
                            location.createdAt || location.changeDatetime,
                          ).format("MMM DD, YYYY")}
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          {/* <View style={classes.contentSection}>
          <Text style={classes.sectionTitle}>Recent Activity</Text>
          <Text style={classes.sectionText}>
            {activeAlertsArray.length > 0
              ? `You have ${activeAlertsArray.length} alert${activeAlertsArray.length !== 1 ? "s" : ""} to review.`
              : "No active alerts at this time."
            }
            {teamList.length > 0 && ` Your organization has ${teamList.length} team${teamList.length !== 1 ? "s" : ""} configured.`}
            {locationList.length > 0 && ` You"re managing ${locationList.length} location${locationList.length !== 1 ? "s" : ""}.`}
          </Text>
        </View> */}
        </View>

        <View style={classes.rightColumn}>
          {staleInfoAlerts.length > 0 && (
            <View style={classes.staleBanner}>
              <Text style={classes.staleText}>
                {staleInfoAlerts.length} info message
                {staleInfoAlerts.length > 1 ? "s have" : " has"} passed their
                expiration date and may need attention.
              </Text>
              <CustomButton
                title="Resolve"
                variant="danger"
                icon="check-all"
                onPress={handleBatchResolve}
              />
            </View>
          )}
          <View style={classes.alertsContainer}>
            <Text style={[classes.alertsHeader, { color: organizationColor }]}>
              Alerts
            </Text>
            {activeAlertsArray.length > 0 ? (
              activeAlertsArray.map((alert) => {
                const stateClasses = StyleSheet.create({
                  alertCard: {
                    ...classes.alertCard,
                    borderColor: alert.isActive
                      ? organizationColor
                      : theme.color.darkGrey,
                  },
                  alertTitle: {
                    ...classes.alertTitle,
                    color: alert.isActive
                      ? organizationColor
                      : theme.color.darkGrey,
                  },
                });

                const affectedSystems =
                  alert._associations?.systemAlertMap
                    ?.map(
                      (systemAlert) =>
                        systemAlert._associations?.organizationSystem,
                    )
                    .filter(Boolean) || [];

                const affectedTeams =
                  alert._associations?.teamAlertMap
                    ?.map((teamAlert) =>
                      teamList.find((team) => team.teamId === teamAlert.teamId),
                    )
                    .filter(Boolean) || [];

                const affectedLocations =
                  alert._associations?.locationAlertMap
                    ?.map((locationAlert) =>
                      locationList.find(
                        (location) =>
                          location.locationId === locationAlert.locationId,
                      ),
                    )
                    .filter(Boolean) || [];

                const uniqueRecipients = new Set();

                if (alert._associations?.recipientObjects) {
                  alert._associations.recipientObjects.forEach((recipient) => {
                    if (recipient.personId) {
                      uniqueRecipients.add(recipient.personId);
                    }
                  });
                }

                affectedTeams.forEach((team) => {
                  if (team._associations?.TeamPersons) {
                    team._associations.TeamPersons.forEach((teamPerson) => {
                      if (teamPerson.personId) {
                        uniqueRecipients.add(teamPerson.personId);
                      }
                    });
                  }
                });

                // Add people from affected locations
                affectedLocations.forEach((location) => {
                  if (location._associations?.LocationPersons) {
                    location._associations.LocationPersons.forEach(
                      (locationPerson) => {
                        if (locationPerson.personId) {
                          uniqueRecipients.add(locationPerson.personId);
                        }
                      },
                    );
                  }
                });

                const totalRecipients = uniqueRecipients.size;

                const isExpired =
                  alert.type === ALERT_TYPE_INFO &&
                  alert.isActive &&
                  alert.expirationDatetime &&
                  new Date(alert.expirationDatetime) < new Date();

                return (
                  <Pressable
                    key={alert.alertId}
                    style={({ pressed }) => [
                      stateClasses.alertCard,
                      {
                        borderWidth: 2,
                        borderColor: organizationColor,
                      },
                      pressed && classes.alertCardPressed,
                    ]}
                    onPress={() => handleAlertPress(alert.alertId, alert.type)}
                  >
                    <View style={classes.alertTitleRow}>
                      <Text style={[stateClasses.alertTitle, classes.alertTitleText]}>
                        {alert.title}
                        {!alert.isActive && (
                          <Text style={classes.dismissedText}> (dismissed)</Text>
                        )}
                      </Text>
                      {isExpired && (
                        <View style={classes.expiredBadge}>
                          <Text style={classes.expiredBadgeText}>Expired</Text>
                        </View>
                      )}
                    </View>

                    {alert.description && (
                      <Text style={classes.alertDescription} numberOfLines={2}>
                        {stripHtml(alert.description)}
                      </Text>
                    )}

                    <View style={classes.alertInfo}>
                      {affectedSystems.length > 0 && (
                        <View style={classes.alertInfoRow}>
                          <DataIcon
                            style={classes.alertInfoIcon}
                            fill={organizationColor}
                          />
                          <Text style={classes.alertInfoText}>
                            {affectedSystems.length} platform
                            {affectedSystems.length !== 1 ? "s" : ""}:{" "}
                            {affectedSystems
                              .map((thisSys) => thisSys.title)
                              .join(", ")}
                          </Text>
                        </View>
                      )}

                      {affectedTeams.length > 0 && (
                        <View style={classes.alertInfoRow}>
                          <TeamIcon
                            style={classes.alertInfoIcon}
                            fill={organizationColor}
                          />
                          <Text style={classes.alertInfoText}>
                            {affectedTeams.length} team
                            {affectedTeams.length !== 1 ? "s" : ""}:{" "}
                            {affectedTeams
                              .map((thisTeam) => thisTeam.name)
                              .join(", ")}
                          </Text>
                        </View>
                      )}

                      {affectedLocations.length > 0 && (
                        <View style={classes.alertInfoRow}>
                          <LocationIcon
                            style={classes.alertInfoIcon}
                            fill={organizationColor}
                          />
                          <Text style={classes.alertInfoText}>
                            {affectedLocations.length} location
                            {affectedLocations.length !== 1 ? "s" : ""}:{" "}
                            {affectedLocations
                              .map((thisLocation) => thisLocation.name)
                              .join(", ")}
                          </Text>
                        </View>
                      )}

                      {totalRecipients > 0 && (
                        <View style={classes.alertInfoRow}>
                          <ContactIcon
                            style={classes.alertInfoIcon}
                            fill={organizationColor}
                          />
                          <Text style={classes.alertInfoText}>
                            {totalRecipients} recipient
                            {totalRecipients !== 1 ? "s" : ""}
                          </Text>
                        </View>
                      )}
                    </View>

                    <Text style={classes.alertMeta}>
                      {moment(alert.startDatetime).format(
                        "MMM DD, YYYY hh:mm A",
                      )}
                    </Text>

                    {alert.isActive && (
                      <View style={classes.alertButtonContainer}>
                        <CustomButton
                          title="Resolve"
                          variant="dark"
                          icon="check-circle"
                          onPress={(event) => {
                            event.stopPropagation();
                            handleResolveAlert(alert);
                          }}
                        />
                      </View>
                    )}
                  </Pressable>
                );
              })
            ) : (
              <Text style={classes.noAlertsText}>No active alerts</Text>
            )}
          </View>
        </View>
      </View>

      <CustomModal
        open={resolvingAlert !== null}
        onClose={() => {
          setResolvingAlert(null);
          setResolutionText("");
        }}
        title={resolvingAlert?.type === ALERT_TYPE_INFO ? "Resolve Info Message" : "Resolve Alert"}
        onSave={handleConfirmResolve}
        canSave={true}
        saveTitle="Resolve"
      >
        <View style={{ padding: 20, minWidth: 400 }}>
          <LabelInput
            label="Please provide a resolution:"
            value={resolutionText}
            onChangeText={setResolutionText}
            placeholder="Describe how this alert was resolved..."
            multiline
            numberOfLines={6}
            style={{ height: 150 }}
          />
        </View>
      </CustomModal>
    </ScrollView>
  );
}
